// src/constants/chartColors.js
//
// Centralized chart color assignments.
// - Prefer explicit colors for known doc IDs (school programs + future town docs).
// - Fall back to a large, high-contrast palette for anything else.
//
// Notes:
// - Keys should match the "slice.key" you pass into charts.
//   For BoE program pie: keys are docIds like "0200", "0300", etc.
//   For future town docs: keys are strings like "revenues_intergovernmental", etc.

export const CHART_COLOR_MAP = {
  // ----------------------------
  // Board of Ed program docs
  // ----------------------------
  "0200": "#4E79A7",
  "0300": "#F28E2B",
  "0400": "#E15759",
  "0500": "#76B7B2",
  "0600": "#59A14F",
  "0700": "#EDC948",
  "0900": "#B07AA1",
  "1000": "#FF9DA7",
  "1100": "#9C755F",
  "1300": "#1B9E77",
  "1400": "#D95F02",
  "1500": "#7570B3",
  "1600": "#E7298A",
  "1900": "#66A61E",
  "2000": "#E6AB02",
  "2100": "#A6761D",
  "2200": "#8DD3C7",
  "2300": "#80B1D3",
  "2400": "#FDB462",
  "2700": "#B3DE69",
  "2800": "#BC80BD",
  "3000": "#FB8072",
  "3100": "#BEBADA",
  "3200": "#FFED6F",
  "3300": "#6A3D9A",
  "3400": "#33A02C",
  "3600": "#1F78B4",

  // ----------------------------
  // Town-side docs (future-proof)
  // ----------------------------
  expenditures_administrative_services: "#A6CEE3",
  expenditures_community_development: "#B2DF8A",
  expenditures_debt_service: "#FB9A99",
  expenditures_debt_transfers_out: "#FDBF6F",

  revenues_charges_for_services: "#CAB2D6",
  revenues_intergovernmental: "#FF7F00",
  revenues_licenses_permits: "#E31A1C",
  revenues_other_revenues: "#B15928",
  revenues_tax_revenues: "#2C7FB8",
  revenues_transfers_in_from: "#7FCDBB",
};

// Large fallback palette (kept intentionally distinct and varied).
// Used if a slice key is not in CHART_COLOR_MAP.
export const CHART_FALLBACK_COLORS = [
  "#4E79A7",
  "#F28E2B",
  "#E15759",
  "#76B7B2",
  "#59A14F",
  "#EDC948",
  "#B07AA1",
  "#FF9DA7",
  "#9C755F",
  "#BAB0AC",

  "#1B9E77",
  "#D95F02",
  "#7570B3",
  "#E7298A",
  "#66A61E",
  "#E6AB02",
  "#A6761D",
  "#666666",

  "#8DD3C7",
  "#FFFFB3",
  "#BEBADA",
  "#FB8072",
  "#80B1D3",
  "#FDB462",
  "#B3DE69",
  "#FCCDE5",
  "#D9D9D9",
  "#BC80BD",
  "#CCEBC5",
  "#FFED6F",

  "#A6CEE3",
  "#1F78B4",
  "#B2DF8A",
  "#33A02C",
  "#FB9A99",
  "#E31A1C",
  "#FDBF6F",
  "#FF7F00",
  "#CAB2D6",
  "#6A3D9A",
];

// DJB2-ish hash (same idea as your current code, just centralized)
export function hashStringToInt(str) {
  let h = 5381;
  const s = String(str ?? "");
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 33) ^ s.charCodeAt(i);
  }
  return h >>> 0;
}

/**
 * Deterministic color:
 * 1) Explicit map by ID (best)
 * 2) Hash into CHART_FALLBACK_COLORS (stable for unknown keys)
 */
export function getChartColor(keyOrLabel, opts = {}) {
  const basis = String(keyOrLabel ?? "").trim();
  const debug = opts?.debug === true;

  if (basis && CHART_COLOR_MAP[basis]) {
    if (debug) console.debug("[getChartColor] MAP", { basis, color: CHART_COLOR_MAP[basis] });
    return CHART_COLOR_MAP[basis];
  }

  const idx = CHART_FALLBACK_COLORS.length
    ? hashStringToInt(basis || "fallback") % CHART_FALLBACK_COLORS.length
    : 0;

  const color = CHART_FALLBACK_COLORS[idx] || "#999999";

  if (debug) {
    console.debug("[getChartColor] FALLBACK", {
      basis,
      idx,
      color,
      paletteSize: CHART_FALLBACK_COLORS.length,
    });
  }

  return color;
}